/**
 * **How the Grafik tab WRITES its hours** — a window's refusal, a line of „Inaczej niż zwykle", a
 * day of its month — in mobile's words (`booking.trainer.*`, carried under `web:`) where mobile has
 * them, and in the T-B2 canon's where it has not.
 *
 * Pure: no React, no DOM, no query hook, no i18next — `t` and the locale arrive as parameters.
 */

import type { TFunction } from 'i18next';

import type { Deviation, DeviationMark } from './scheduleDeviations';
import { clockOf, isoWeekday, type CivilDay } from './trainerClock';
import type { WeekPattern, WindowProblem } from './weekPattern';
import { compactSpan, type WorkingWindow } from './workingHours';

const TRAINER = 'web:booking.trainer';
const SCHEDULE = 'web:booking.schedule';

/**
 * `Pt 2.10` — a day in the Grafik tab's list: the app's own short weekday (`Nd`, never Intl's
 * `niedz.`), the day unpadded, the month padded.
 */
export function listDay(day: CivilDay, t: TFunction): string {
  return `${t(`web:weekdays.short.${String(isoWeekday(day))}`)} ${String(Number(day.slice(8, 10)))}.${day.slice(5, 7)}`;
}

/** `7:00–12:00` — a window as the cards write it: always both clocks, never compact. */
export function windowSpan(window: WorkingWindow): string {
  return `${clockOf(window.start)}–${clockOf(Math.min(window.end, 1440))}`;
}

/** `7:00–12:00, 16:00–20:00`. */
export function windowsSpan(windows: readonly WorkingWindow[]): string {
  return windows.map(windowSpan).join(', ');
}

/** The one sentence under two windows that fight — what is wrong and what to do about it. */
export function windowProblemText(problem: WindowProblem, t: TFunction): string {
  switch (problem.code) {
    case 'window_overlap':
      return t(`${TRAINER}.windowError.window_overlap.text`, {
        from: clockOf(problem.from),
        to: clockOf(problem.to),
        merged: windowSpan({ start: problem.mergedStart, end: problem.mergedEnd }),
      });
    case 'window_crosses_midnight':
      return t(`${TRAINER}.windowError.window_crosses_midnight.text`);
    case 'window_empty':
      return t(`${TRAINER}.windowError.window_empty.text`);
  }
}

/**
 * A whole week in one line — „Tam: pon–czw 7:00–12:00, 16:00–20:00 · pt 9:00–12:00, reszta
 * wolne" — the week saved elsewhere, as the card names it beside the trainer's own draft. Running
 * days with the same hours share one span of weekdays; the free days are „the rest".
 */
export function weekLine(pattern: WeekPattern, t: TFunction, locale: string): string {
  const day = (weekday: number) => lowerFirst(t(`web:weekdays.short.${String(weekday)}`), locale);
  const runs: { first: number; last: number; hours: string }[] = [];
  for (const entry of pattern.days) {
    const hours = windowsSpan(entry.windows);
    const previous = runs.at(-1);
    if (hours !== '' && previous?.hours === hours && previous.last === entry.weekday - 1) {
      previous.last = entry.weekday;
    } else if (hours !== '') {
      runs.push({ first: entry.weekday, last: entry.weekday, hours });
    }
  }
  if (runs.length === 0) return t(`${SCHEDULE}.drift.allFree`);
  const worked = runs
    .map(
      (run) =>
        `${run.first === run.last ? day(run.first) : `${day(run.first)}–${day(run.last)}`} ${run.hours}`,
    )
    .join(' · ');
  const free = pattern.days.some((entry) => entry.windows.length === 0);
  return t(`${SCHEDULE}.drift.there`, {
    week: free ? t(`${SCHEDULE}.drift.restFree`, { days: worked }) : worked,
  });
}

function lowerFirst(value: string, locale: string): string {
  return `${value.charAt(0).toLocaleLowerCase(locale)}${value.slice(1)}`;
}

/** One line of „Inaczej niż zwykle": what that day or stretch is, and what it is instead of. */
export function deviationWords(
  deviation: Deviation,
  t: TFunction,
  locale: string,
): { readonly title: string; readonly meta: string } {
  if (deviation.kind === 'exception') {
    const what =
      deviation.windows.length === 0
        ? t(`${TRAINER}.schedule.deviation.closedTitle`)
        : t(`${TRAINER}.schedule.deviation.onlyTitle`, { windows: windowsSpan(deviation.windows) });
    const meta =
      deviation.usual.length > 0
        ? t(`${TRAINER}.schedule.deviation.insteadMeta`, { windows: windowsSpan(deviation.usual) })
        : deviation.windows.length === 0
          ? t(`${TRAINER}.schedule.deviation.closedMeta`)
          : t(`${TRAINER}.schedule.deviation.addedMeta`);
    return { title: `${listDay(deviation.date, t)} · ${what}`, meta };
  }
  const note = deviation.block.note;
  const meta =
    note === null
      ? t(`${TRAINER}.schedule.deviation.blockTitle`)
      : t(`${SCHEDULE}.blockMeta`, { note });
  if (deviation.fromDay === deviation.toDay) {
    return {
      title: t(`${SCHEDULE}.blockSameDay`, {
        day: listDay(deviation.fromDay, t),
        span: windowSpan({ start: deviation.fromMinutes, end: deviation.toMinutes }),
      }),
      meta,
    };
  }
  return {
    title: t(`${SCHEDULE}.blockSpan`, {
      from: listDay(deviation.fromDay, t),
      to: lowerFirst(listDay(deviation.toDay, t), locale),
      fromClock: clockOf(deviation.fromMinutes),
      toClock: clockOf(deviation.toMinutes),
    }),
    meta,
  };
}

/** A day of the card's month: `Tylko 9–12`, `Zamknięte`, or nothing. */
export function markWords(mark: DeviationMark | undefined, t: TFunction): string | null {
  if (mark?.exception == null) return null;
  return mark.exception.windows.length === 0
    ? t(`${SCHEDULE}.cellClosed`)
    : t(`${SCHEDULE}.cellOnly`, { range: mark.exception.windows.map(compactSpan).join(', ') });
}
